import { Dispatch, SetStateAction, useEffect, useState } from 'react';

/** Safe JSON read from localStorage; returns `fallback` on miss or error. */
export function readJSON<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored != null ? (JSON.parse(stored) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Safe JSON write to localStorage; swallows private-mode / quota errors. */
export function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore (private mode / quota exceeded) */
  }
}

/**
 * `useState` that persists its value in `localStorage` under `key`, so view
 * preferences (selected tab, grouping, filter…) survive reloads and revisits.
 *
 * Drop-in for `useState`: same tuple + setter signature (value or updater).
 * Reads/writes are guarded so private-mode or quota errors fall back to
 * in-memory state instead of crashing.
 */
export function usePersistentState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => readJSON(key, defaultValue));

  useEffect(() => {
    writeJSON(key, state);
  }, [key, state]);

  return [state, setState];
}
